import { bcp47, type Language } from "./languages"

// Intl-backed formatting helpers (DEV-9538). Constructing an `Intl.*Format` is
// ~100× the cost of a `.format()` call, so every formatter is memoized at module
// scope keyed by its full construction signature. NEVER construct per render.

const currencyCache = new Map<string, Intl.NumberFormat>()
const numberCache = new Map<string, Intl.NumberFormat>()
const dateCache = new Map<string, Intl.DateTimeFormat>()

const currencyFormatter = (
  locale: string,
  currencyCode: string,
  fractionDigits: number | undefined
): Intl.NumberFormat => {
  // The key mirrors the `Intl.NumberFormat` construction signature exactly, so
  // nothing that changes the formatter can collide in this process-lifetime
  // cache. The glyph is deliberately absent: `formatCurrency` substitutes it
  // AFTER formatting, so it never varies the cached instance.
  const key = `${locale}|${currencyCode}|${fractionDigits ?? "default"}`
  let fmt = currencyCache.get(key)
  if (!fmt) {
    fmt = new Intl.NumberFormat(locale, {
      style: "currency",
      currency: currencyCode,
      // Pin the fraction digits when the caller specifies them (e.g. a
      // `decimals={0}` price); otherwise Intl uses the currency's own default.
      ...(fractionDigits != null
        ? {
            minimumFractionDigits: fractionDigits,
            maximumFractionDigits: fractionDigits,
          }
        : {}),
    })
    currencyCache.set(key, fmt)
  }
  return fmt
}

/**
 * U+2060 WORD JOINER: zero-width, forbids a line break at its position. Keeps
 * a leading currency glyph welded to its digits so a price can never wrap
 * mid-value. Legacy `Money` emitted this as `&#8288;`.
 */
const WORD_JOINER = "\u2060"

/**
 * The part types that stand where the digits go. `integer` is the finite case;
 * `Intl` substitutes `nan`/`infinity` for a non-finite amount. All three have to
 * count, or `symbolLeads` reads false for `NaN`/`Infinity` and the prefix rule
 * silently inverts: the word joiner is skipped, and any gap literal the ICU
 * build emits between glyph and value survives instead of being dropped. Node's
 * current ICU happens to omit that literal for non-finite values, so only the
 * joiner misbehaves today \u2014 but that is an ICU detail, not a guarantee.
 */
const VALUE_PART_TYPES = new Set<Intl.NumberFormatPartTypes>([
  "integer",
  "nan",
  "infinity",
])

/**
 * Locale-correct currency string. `Intl` owns grouping (`1,234.56`), the
 * decimal separator, symbol PLACEMENT (`99,99 €` in German), and 0-decimal
 * currencies. The caller owns the glyph.
 *
 * `value` is a MAJOR-unit amount (e.g. `12.5` = 12.50), NOT cents. Callers that
 * hold cents divide by 100 first (matches `Money`'s existing convention).
 *
 * DEV-9893: the value is built from `formatToParts` and the `currency` part is
 * swapped for `opts.symbol` before rejoining. Letting `Intl` pick the glyph is
 * not an option: it drops a currency's disambiguating prefix whenever the
 * locale's region matches the currency's (bare `en` renders AUD as `A$`, and
 * `en-AU` renders it as a plain `$`), which would make AU/CA/NZ prices read as
 * USD. Omit `opts.symbol` and `Intl`'s own glyph is kept, unchanged.
 *
 * `opts.symbol` is the glyph, from `marketsData`. `opts.noSymbol` drops it
 * along with the literal separating it from the digits. `opts.fractionDigits`
 * pins the decimal places, defaulting to the currency's own. `opts.wordJoiner`
 * is off by default because U+2060 is invisible but real: only rendered output
 * should ask for it, never an analytics or aria string.
 */
export const formatCurrency = (
  value: number,
  currencyCode: string,
  language: Language,
  opts?: {
    noSymbol?: boolean
    fractionDigits?: number
    symbol?: string
    wordJoiner?: boolean
  }
): string => {
  const noSymbol = opts?.noSymbol ?? false
  const symbol = opts?.symbol
  const parts = currencyFormatter(
    bcp47(language),
    currencyCode,
    opts?.fractionDigits
  ).formatToParts(value)

  const at = parts.findIndex((part) => part.type === "currency")
  // Every `style: "currency"` format emits a currency part; without one the
  // index arithmetic below has nothing to anchor on, so rejoin verbatim.
  if (at === -1) return parts.map((part) => part.value).join("")

  // Is the glyph a prefix (`$1,234.56`) or a suffix (`1.234,56 €`)? Drives both
  // the word joiner and which literal counts as the symbol/digit gap.
  const symbolLeads = parts.some(
    (part, i) => i > at && VALUE_PART_TYPES.has(part.type)
  )

  const out: string[] = []
  for (const [i, part] of parts.entries()) {
    if (i === at) {
      if (noSymbol) continue
      out.push(symbol ?? part.value)
      if (opts?.wordJoiner && symbolLeads) out.push(WORD_JOINER)
      continue
    }
    if (part.type === "literal" && (i === at - 1 || i === at + 1)) {
      // With the glyph gone, so is the gap that separated it (matches the old
      // `.replace(code, "").trim()` behaviour byte-for-byte).
      if (noSymbol) continue
      // A caller-supplied LEADING glyph owns its own gap: `marketsData` bakes
      // the convention into the glyph itself ("CHF" has none, an unsold code
      // falls back to "SEK " with a trailing space), and legacy `Money`
      // rendered `symbol + value` with nothing between, so dropping the U+00A0
      // `Intl` wants here is what keeps Switzerland on `CHF123.99`. A TRAILING
      // glyph's separator belongs to the locale (German's U+00A0 before `€`)
      // and must survive.
      if (symbolLeads && i === at + 1 && symbol != null) continue
    }
    out.push(part.value)
  }

  return out.join("")
}

/** Locale-correct number formatting (grouping + decimals). */
export const formatNumber = (
  value: number,
  language: Language,
  opts?: Intl.NumberFormatOptions
): string => {
  const key = `${language}|${JSON.stringify(opts ?? {})}`
  let fmt = numberCache.get(key)
  if (!fmt) {
    fmt = new Intl.NumberFormat(bcp47(language), opts)
    numberCache.set(key, fmt)
  }
  return fmt.format(value)
}

/** Locale-correct date formatting. Replaces hardcoded `en-GB`/`en-US`. */
export const formatDate = (
  value: Date | number | string,
  language: Language,
  opts?: Intl.DateTimeFormatOptions
): string => {
  const date = value instanceof Date ? value : new Date(value)
  // A malformed string/number yields an Invalid Date, and `Intl` throws a
  // RangeError formatting it. Return "" instead so bad upstream data degrades
  // to empty text rather than crashing the render.
  if (Number.isNaN(date.getTime())) return ""
  const key = `${language}|${JSON.stringify(opts ?? {})}`
  let fmt = dateCache.get(key)
  if (!fmt) {
    fmt = new Intl.DateTimeFormat(bcp47(language), opts)
    dateCache.set(key, fmt)
  }
  return fmt.format(date)
}

const pluralRulesCache = new Map<Language, Intl.PluralRules>()

// Shared with `t.ts` (`resolvePlural`) so there's a single memoized
// `Intl.PluralRules` instance per language, not one per call site.
export const pluralRulesFor = (language: Language): Intl.PluralRules => {
  let rules = pluralRulesCache.get(language)
  if (!rules) {
    rules = new Intl.PluralRules(bcp47(language))
    pluralRulesCache.set(language, rules)
  }
  return rules
}

/**
 * Resolve a value against `Intl.PluralRules` category strings. Prefer catalog
 * plural keys via `getT` for user-facing copy; this raw helper is for the few
 * ad-hoc call sites that select among inline strings.
 */
export const plural = (
  count: number,
  language: Language,
  forms: { other: string } & Partial<Record<Intl.LDMLPluralRule, string>>
): string => {
  const category = pluralRulesFor(language).select(count)
  return forms[category] ?? forms.other
}
